import React, { useEffect, useRef, useState } from 'react';
import { Plus, Image as ImageIcon, Music, Clapperboard, BookOpen, Workflow } from 'lucide-react';
import { TYPE } from '../../constants/typeScale';

/**
 * "+ Create" discovery menu — the native (non-ad) answer to "users don't know they can make songs,
 * videos, images…". Each option seeds the composer with a starter phrase; the existing intent
 * detection + generation pickers (dimension/song type) then appear, so this just makes the
 * capabilities DISCOVERABLE without a promo card. Monochrome, Lucide-only.
 */
interface CreateOption { key: string; label: string; icon: React.FC<{ className?: string }>; starter: string }

const OPTIONS: CreateOption[] = [
  { key: 'image', label: 'Image', icon: ImageIcon, starter: 'Make an image of ' },
  { key: 'song', label: 'Song', icon: Music, starter: 'Write a song about ' },
  { key: 'video', label: 'Video', icon: Clapperboard, starter: 'Make a short video of ' },
  { key: 'story', label: 'Story', icon: BookOpen, starter: 'Write a short story about ' },
  { key: 'diagram', label: 'Diagram', icon: Workflow, starter: 'Make a diagram of ' },
];

const CreateMenu: React.FC<{ onPick: (starter: string) => void; onVideoStudio?: () => void }> = ({ onPick, onVideoStudio }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Create something"
        title="Create an image, song, video, story or diagram"
        className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-secondary-500 hover:text-secondary-800 dark:hover:text-secondary-200 hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors"
      >
        <Plus className="w-4 h-4" />
      </button>
      {open && (
        <div className="absolute bottom-full left-0 mb-2 w-44 bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-600 rounded-xl shadow-xl overflow-hidden z-10">
          <p className={`px-3 py-1.5 ${TYPE.label} text-secondary-400 flex items-center gap-1`}>
            <Plus className="w-3 h-3" /> Create
          </p>
          {OPTIONS.map((o) => (
            <button
              key={o.key}
              type="button"
              onClick={() => {
                // Video opens the multi-shot Video Studio workflow; the rest seed the composer for inline generation.
                if (o.key === 'video' && onVideoStudio) onVideoStudio();
                else onPick(o.starter);
                setOpen(false);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-secondary-100 dark:hover:bg-secondary-700"
            >
              <o.icon className="w-4 h-4 text-secondary-500 dark:text-secondary-300" />
              <span className={`${TYPE.control} text-secondary-900 dark:text-white`}>{o.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CreateMenu;
